// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { Appointment, HealthcareService, Practitioner, Schedule, Slot } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import {
  SCHEDULING_V2_PROVENANCE_TAG,
  validateSchedulingScope,
  validateScopedResource,
  validateTenantScopedResource,
  withTenantAndEnvironmentScope,
} from '@polarissleep/scheduling-next';
import {
  buildOverrideSlot,
  buildScheduleV2Availability,
  calculateOverrideSlotChanges,
  getOverrideSlotDraft,
  getPractitionerTimezone,
  getServiceCode,
  localDateTimeToFhir,
  type OverrideSlotChanges,
  validateAvailabilityDraft,
  withPractitionerTimezone,
} from './availability';
import {
  POLARIS_ENV_TAG_SYSTEM,
  SERVICE_TYPE_REFERENCE_EXTENSION_URL,
  V2_OVERRIDE_TAG_CODE,
  V2_OVERRIDE_TAG_SYSTEM,
} from './constants';
import type {
  AppointmentConflict,
  AvailabilityOverrideDraft,
  V2AvailabilityDraft,
  V2AvailabilityResources,
} from './types';

const MAX_SCHEDULE_SEARCH_COUNT = 2;

function withV2SlotTag(slot: Slot): Slot {
  return {
    ...slot,
    meta: {
      ...slot.meta,
      tag: [
        ...(slot.meta?.tag ?? []).filter(
          (tag) => tag.system !== SCHEDULING_V2_PROVENANCE_TAG.system || tag.code !== SCHEDULING_V2_PROVENANCE_TAG.code
        ),
        SCHEDULING_V2_PROVENANCE_TAG,
      ],
    },
  };
}

export interface V2AvailabilityRepository {
  loadResources(
    practitioner: Practitioner,
    appointmentTypes: AppointmentTypeDefinition[]
  ): Promise<V2AvailabilityResources>;
  loadPastOverrideSlots(
    practitioner: Practitioner,
    schedule: Schedule,
    healthcareServices: HealthcareService[]
  ): Promise<Slot[]>;
  findAppointmentConflicts(
    practitioner: Practitioner,
    draft: V2AvailabilityDraft,
    existingOverrideSlots?: Slot[]
  ): Promise<AppointmentConflict[]>;
  saveResources(
    resources: V2AvailabilityResources,
    draft: V2AvailabilityDraft,
    organizationId: string | undefined,
    environment: string | undefined
  ): Promise<V2AvailabilityResources>;
}

export function createV2AvailabilityRepository(medplum: MedplumClient): V2AvailabilityRepository {
  return {
    async loadResources(practitioner, appointmentTypes) {
      const organizationId = import.meta.env?.VITE_MEDPLUM_ORGANIZATION_ID;
      const environment = import.meta.env?.VITE_POLARIS_ENV;
      if (!organizationId || !environment) {
        throw new Error('Tenant organization and Polaris environment are required for v2 availability.');
      }
      const scope = { organizationId, environment };
      validateSchedulingScope(scope);
      validateTenantScopedResource(practitioner, 'Practitioner', scope);
      validatePractitionerEnvironment(practitioner, environment);
      const compartment = `Organization/${organizationId}`;
      const appointmentCodes = new Set(
        appointmentTypes.filter((type) => type.coding.code).map((type) => `${type.coding.system}|${type.coding.code}`)
      );
      const [schedule, services] = await Promise.all([
        practitioner.id
          ? searchUniqueSchedule(medplum, `Practitioner/${practitioner.id}`, organizationId, environment)
          : undefined,
        medplum.searchResources('HealthcareService', {
          active: 'true',
          _compartment: compartment,
          _tag: `${POLARIS_ENV_TAG_SYSTEM}|${environment}`,
          _count: '1000',
        }) as Promise<HealthcareService[]>,
      ]);
      if (schedule) {
        validateScopedResource(schedule, 'Schedule', scope);
        validateScheduleActor(schedule, practitioner);
      }
      const scopedServices = services.filter((service) => hasSingleEnvironmentTag(service, environment));
      for (const service of scopedServices) {
        validateScopedResource(service, 'HealthcareService', scope);
      }
      const healthcareServices = scopedServices.filter((service) => {
        const coding = service.type?.flatMap((type) => type.coding ?? []).find((item) => item.code);
        return coding && appointmentCodes.has(`${coding.system}|${coding.code}`) && Boolean(service.id);
      });
      const overrideSlots = schedule?.id
        ? await loadV2OverrideSlots(
            medplum,
            schedule,
            organizationId,
            environment,
            healthcareServices,
            practitioner,
            undefined
          )
        : [];
      if (overrideSlots.length > 0) {
        const timezone = getPractitionerTimezone(practitioner);
        for (const slot of overrideSlots) {
          const isProtectedStatus = Boolean(slot.status && protectedOverrideSlotStatuses.has(slot.status));
          if (!getOverrideSlotDraft(slot, timezone, healthcareServices) && !isProtectedStatus) {
            throw new Error(
              `V2 override Slot/${slot.id ?? 'unknown'} cannot map to an active HealthcareService in provider timezone ${timezone}; fix its serviceType, start, or end before saving.`
            );
          }
        }
      }
      return { practitioner, schedule, healthcareServices, overrideSlots };
    },

    async loadPastOverrideSlots(practitioner, schedule, healthcareServices) {
      const organizationId = import.meta.env?.VITE_MEDPLUM_ORGANIZATION_ID;
      const environment = import.meta.env?.VITE_POLARIS_ENV;
      if (!organizationId || !environment) {
        throw new Error('Tenant organization and Polaris environment are required for v2 availability.');
      }
      validateSchedulingScope({ organizationId, environment });
      validateTenantScopedResource(practitioner, 'Practitioner', { organizationId, environment });
      validatePractitionerEnvironment(practitioner, environment);
      validateScopedResource(schedule, 'Schedule', { organizationId, environment });
      validateScheduleActor(schedule, practitioner);
      for (const service of healthcareServices) {
        validateScopedResource(service, 'HealthcareService', { organizationId, environment });
      }
      return loadV2OverrideSlots(
        medplum,
        schedule,
        organizationId,
        environment,
        healthcareServices,
        practitioner,
        `lt${getDateInTimezone(getPractitionerTimezone(practitioner))}`
      );
    },

    async findAppointmentConflicts(practitioner, draft, existingOverrideSlots = []) {
      return findAppointmentConflictsInternal(medplum, practitioner, draft, existingOverrideSlots);
    },

    async saveResources(resources, draft, organizationId, environment) {
      if (!resources.practitioner.id) {
        throw new Error('Provider profile must have an ID before saving availability.');
      }
      if (!organizationId || !environment) {
        throw new Error('Tenant organization and Polaris environment are required for v2 availability.');
      }
      validateSchedulingScope({ organizationId, environment });
      validateTenantScopedResource(resources.practitioner, 'Practitioner', { organizationId, environment });
      validatePractitionerEnvironment(resources.practitioner, environment);
      if (resources.schedule) {
        validateScopedResource(resources.schedule, 'Schedule', { organizationId, environment });
        validateScheduleActor(resources.schedule, resources.practitioner);
      }
      for (const service of resources.healthcareServices) {
        validateScopedResource(service, 'HealthcareService', { organizationId, environment });
      }
      for (const slot of resources.overrideSlots) {
        validateScopedResource(slot, 'Slot', { organizationId, environment });
        if (resources.schedule) {
          validateOverrideSlotSchedule(slot, resources.schedule);
        }
      }
      validateAvailabilityDraft(draft.weeklyAvailability, draft.overrides, {
        healthcareServices: resources.healthcareServices,
        serviceId: draft.healthcareServiceId,
        timezone: draft.timezone,
        schedule: resources.schedule,
        existingOverrideSlots: resources.overrideSlots,
      });
      const conflicts = draft.overrides.some((override) => override.status === 'busy-unavailable')
        ? await findAppointmentConflictsInternal(medplum, resources.practitioner, draft, resources.overrideSlots)
        : [];
      if (conflicts.length > 0 && !draft.conflictAcknowledged) {
        throw new Error('Appointment conflicts must be acknowledged before saving provider availability.');
      }
      const scheduleDraft: Schedule = resources.schedule ?? {
        resourceType: 'Schedule',
        actor: [{ reference: `Practitioner/${resources.practitioner.id}` }],
        active: true,
      };
      const scheduleToSave = withTenantAndEnvironmentScope(
        buildScheduleV2Availability(scheduleDraft, resources.healthcareServices, draft),
        organizationId,
        environment
      );
      const loadedOverrideIds = new Set(resources.overrideSlots.flatMap((slot) => (slot.id ? [slot.id] : [])));
      for (const override of draft.overrides) {
        if (override.id && !loadedOverrideIds.has(override.id)) {
          throw new Error(`Override Slot/${override.id} is not part of the loaded availability resources.`);
        }
      }
      validateOverrideServiceLinks(draft.overrides, resources.healthcareServices, scheduleToSave);

      // Validate the current Slot state before changing the Schedule. The old order
      // updated Schedule first, then discovered a stale/malformed Slot and rolled the
      // Schedule back. That made a valid weekly-hours edit appear to save while the
      // persisted Schedule stayed unchanged.
      const allScheduleSlots =
        scheduleToSave.id && draft.overrides.length > 0
          ? await searchAllScheduleSlots(medplum, scheduleToSave.id, organizationId, environment)
          : [];
      for (const slot of allScheduleSlots.filter(isV2OverrideSlot)) {
        validateV2OverrideSlot(
          slot,
          scheduleToSave,
          getPractitionerTimezone(resources.practitioner),
          resources.healthcareServices,
          { organizationId, environment },
          { allowProtectedStatus: true }
        );
      }

      let desiredSlots: Slot[] = [];
      let changes: OverrideSlotChanges = { create: [], update: [], delete: [] };
      if (scheduleToSave.id) {
        const serviceById = new Map(resources.healthcareServices.map((service) => [service.id, service]));
        desiredSlots = draft.overrides.map((override) => {
          const service = serviceById.get(override.healthcareServiceId);
          if (!service) {
            throw new Error(`Override service ${override.healthcareServiceId} is unavailable.`);
          }
          const current = resources.overrideSlots.find((slot) => slot.id === override.id);
          return buildOverrideSlot(
            scheduleToSave,
            service,
            override,
            draft.timezone,
            organizationId,
            environment,
            current,
            draft.createdBy
          );
        });
        changes = calculateOverrideSlotChanges(resources.overrideSlots, desiredSlots);
        rejectSlotOverlaps(desiredSlots, allScheduleSlots);
        await validateCurrentOverrideSlots(
          medplum,
          resources.overrideSlots,
          changes.update,
          changes.delete,
          { organizationId, environment },
          scheduleToSave
        );
      }

      const rollback: Array<() => Promise<void>> = [];
      try {
        let savedSchedule: Schedule | undefined;
        if (resources.schedule) {
          savedSchedule = await medplum.updateResource<Schedule>(scheduleToSave, ifMatchOptions(resources.schedule));
        } else {
          const scheduleQuery = {
            actor: `Practitioner/${resources.practitioner.id}`,
            _compartment: `Organization/${organizationId}`,
            _tag: `${POLARIS_ENV_TAG_SYSTEM}|${environment}`,
            _count: String(MAX_SCHEDULE_SEARCH_COUNT),
          };
          const createdSchedule = await medplum.createResource<Schedule>(scheduleToSave);
          if (!createdSchedule.id) {
            throw new Error('Schedule create returned no ID; refusing unowned availability save.');
          }
          const createdScheduleId = createdSchedule.id;
          rollback.push(() =>
            medplum.deleteResource('Schedule', createdScheduleId, ifMatchOptions(createdSchedule)).then(() => undefined)
          );
          validateScopedResource(createdSchedule, 'Schedule', { organizationId, environment });
          validateScheduleActor(createdSchedule, resources.practitioner);
          const schedulesAfterCreate = await medplum.searchResources('Schedule', scheduleQuery);
          if (schedulesAfterCreate.length !== 1 || schedulesAfterCreate[0]?.id !== createdScheduleId) {
            throw new Error(
              `Concurrent Schedule resources found for Practitioner/${resources.practitioner.id}; refusing to choose or delete one.`
            );
          }
          savedSchedule = createdSchedule;
        }
        if (!savedSchedule) {
          throw new Error('Schedule save did not return a resource.');
        }
        const savedScheduleForRollback = savedSchedule;
        const originalSchedule = resources.schedule;
        if (originalSchedule) {
          rollback.push(() =>
            medplum.updateResource(originalSchedule, ifMatchOptions(savedScheduleForRollback)).then(() => undefined)
          );
        }
        validateScopedResource(savedSchedule, 'Schedule', { organizationId, environment });
        validateScheduleActor(savedSchedule, resources.practitioner);

        if (!scheduleToSave.id) {
          const serviceById = new Map(resources.healthcareServices.map((service) => [service.id, service]));
          desiredSlots = draft.overrides.map((override) => {
            const service = serviceById.get(override.healthcareServiceId);
            if (!service) {
              throw new Error(`Override service ${override.healthcareServiceId} is unavailable.`);
            }
            return buildOverrideSlot(
              savedSchedule,
              service,
              override,
              draft.timezone,
              organizationId,
              environment,
              undefined,
              draft.createdBy
            );
          });
          changes = calculateOverrideSlotChanges(resources.overrideSlots, desiredSlots);
          await validateCurrentOverrideSlots(
            medplum,
            resources.overrideSlots,
            changes.update,
            changes.delete,
            { organizationId, environment },
            savedSchedule
          );
        }

        let currentTimezone: string | undefined;
        try {
          currentTimezone = getPractitionerTimezone(resources.practitioner);
        } catch {
          currentTimezone = undefined;
        }
        const nextPractitioner = withPractitionerTimezone(resources.practitioner, draft.timezone);
        const hasExpectedEnvironment =
          resources.practitioner.meta?.tag?.filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM).length === 1 &&
          resources.practitioner.meta.tag?.some(
            (tag) => tag.system === POLARIS_ENV_TAG_SYSTEM && tag.code === environment
          ) === true;
        const savedPractitioner =
          currentTimezone !== draft.timezone ||
          !hasExpectedEnvironment ||
          resources.practitioner.extension?.some(
            (extension) => extension.url === 'http://hl7.org/fhir/StructureDefinition/timezone' && extension.valueCode
          ) !== true
            ? await medplum.updateResource<Practitioner>(
                withTenantAndEnvironmentScope(nextPractitioner, organizationId, environment),
                ifMatchOptions(resources.practitioner)
              )
            : resources.practitioner;
        if (savedPractitioner !== resources.practitioner) {
          rollback.push(() =>
            medplum.updateResource(resources.practitioner, ifMatchOptions(savedPractitioner)).then(() => undefined)
          );
        }
        validateTenantScopedResource(savedPractitioner, 'Practitioner', { organizationId, environment });

        for (const slot of desiredSlots) {
          validateOverrideSlotSchedule(slot, savedSchedule);
        }
        for (const slot of changes.create) {
          const saved = await medplum.createResource<Slot>(withV2SlotTag(slot));
          rollback.push(() =>
            saved.id
              ? medplum.deleteResource('Slot', saved.id, ifMatchOptions(saved)).then(() => undefined)
              : Promise.resolve()
          );
          validateScopedResource(saved, 'Slot', { organizationId, environment });
        }
        for (const slot of changes.update) {
          const saved = await medplum.updateResource<Slot>(slot, ifMatchOptions(slot));
          const preimage = resources.overrideSlots.find((candidate) => candidate.id === slot.id);
          if (preimage) {
            rollback.push(() => medplum.updateResource(preimage, ifMatchOptions(saved)).then(() => undefined));
          }
          validateScopedResource(saved, 'Slot', { organizationId, environment });
        }
        for (const slot of changes.delete) {
          if (slot.id) {
            validateOverrideSlotSchedule(slot, savedSchedule);
            await medplum.deleteResource('Slot', slot.id, ifMatchOptions(slot));
            rollback.push(() => medplum.updateResource(slot).then(() => undefined));
          }
        }
        const persistedOverrideSlots = await loadV2OverrideSlots(
          medplum,
          savedSchedule,
          organizationId,
          environment,
          resources.healthcareServices,
          savedPractitioner
        );
        return {
          practitioner: savedPractitioner,
          schedule: savedSchedule,
          healthcareServices: resources.healthcareServices,
          overrideSlots: persistedOverrideSlots,
        };
      } catch (error) {
        const rollbackErrors: string[] = [];
        for (const restore of rollback.reverse()) {
          try {
            await restore();
          } catch (rollbackError) {
            rollbackErrors.push(rollbackError instanceof Error ? rollbackError.message : String(rollbackError));
          }
        }
        if (rollbackErrors.length > 0) {
          throw new Error(
            `${error instanceof Error ? error.message : String(error)}; rollback failed: ${rollbackErrors.join('; ')}`
          );
        }
        throw error;
      }
    },
  };
}

function hasSingleEnvironmentTag(
  resource: { meta?: { tag?: Array<{ system?: string; code?: string }> } },
  environment: string
): boolean {
  const tags = (resource.meta?.tag ?? []).filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM);
  return tags.length === 1 && tags[0].code === environment;
}

async function searchUniqueSchedule(
  medplum: MedplumClient,
  actor: string,
  organizationId: string,
  environment: string
): Promise<Schedule | undefined> {
  const schedules = await medplum.searchResources('Schedule', {
    actor,
    active: 'true',
    _compartment: `Organization/${organizationId}`,
    _tag: `${POLARIS_ENV_TAG_SYSTEM}|${environment}`,
    _count: String(MAX_SCHEDULE_SEARCH_COUNT),
  });
  if (schedules.length > 1) {
    throw new Error(`Multiple Schedule resources found for ${actor}; refusing arbitrary selection.`);
  }
  return schedules[0];
}

function validateScheduleActor(schedule: Schedule, practitioner: Practitioner): void {
  const expectedReference = `Practitioner/${practitioner.id}`;
  if (schedule.actor?.length !== 1 || schedule.actor[0].reference !== expectedReference) {
    throw new Error(`Schedule must reference selected practitioner ${expectedReference}`);
  }
}

function validatePractitionerEnvironment(practitioner: Practitioner, environment: string): void {
  const environmentTags = (practitioner.meta?.tag ?? []).filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM);
  if (environmentTags.length > 1 || (environmentTags.length === 1 && environmentTags[0].code !== environment)) {
    throw new Error(`Practitioner must be scoped to environment ${environment} before saving availability.`);
  }
}

function validateOverrideSlotSchedule(slot: Slot, schedule: Schedule): void {
  const expectedReference = `Schedule/${schedule.id}`;
  if (slot.schedule?.reference !== expectedReference) {
    throw new Error(`Override Slot/${slot.id ?? 'unknown'} must reference selected ${expectedReference}`);
  }
}

function validateOverrideServiceLinks(
  overrides: AvailabilityOverrideDraft[],
  services: HealthcareService[],
  schedule: Schedule
): void {
  const serviceIds = new Set(services.map((service) => service.id));
  const linkedServiceReferences = new Set(
    (schedule.serviceType ?? []).flatMap((serviceType) =>
      (serviceType.extension ?? [])
        .filter((extension) => extension.url === SERVICE_TYPE_REFERENCE_EXTENSION_URL)
        .map((extension) => extension.valueReference?.reference)
        .filter((reference): reference is string => Boolean(reference))
    )
  );
  for (const override of overrides) {
    if (!serviceIds.has(override.healthcareServiceId)) {
      throw new Error(`Override service ${override.healthcareServiceId} is unavailable.`);
    }
    const reference = `HealthcareService/${override.healthcareServiceId}`;
    if (!linkedServiceReferences.has(reference)) {
      throw new Error(`Override service ${override.healthcareServiceId} is not linked to selected Schedule.`);
    }
  }
}

async function loadV2OverrideSlots(
  medplum: MedplumClient,
  schedule: Schedule,
  organizationId: string,
  environment: string,
  healthcareServices: HealthcareService[],
  practitioner: Practitioner,
  startSearch?: string
): Promise<Slot[]> {
  const slots = await searchAllScheduleSlots(medplum, schedule.id, organizationId, environment, startSearch);
  const timezone = getPractitionerTimezone(practitioner);
  return slots.filter((slot) => {
    if (!isV2OverrideSlot(slot)) {
      return false;
    }
    validateV2OverrideSlot(
      slot,
      schedule,
      timezone,
      healthcareServices,
      { organizationId, environment },
      { allowProtectedStatus: true }
    );
    return true;
  });
}

async function searchAllScheduleSlots(
  medplum: MedplumClient,
  scheduleId: string | undefined,
  organizationId: string,
  environment: string,
  startSearch?: string
): Promise<Slot[]> {
  if (!scheduleId) {
    throw new Error('Schedule must have an ID before loading availability Slots.');
  }
  const slots: Slot[] = [];
  for await (const page of medplum.searchResourcePages('Slot', {
    schedule: `Schedule/${scheduleId}`,
    _compartment: `Organization/${organizationId}`,
    _count: 100,
    ...(startSearch ? { start: startSearch } : {}),
  })) {
    slots.push(...(page as Slot[]));
  }
  return slots.filter((slot) => {
    if (!isV2OverrideSlot(slot) && !slot.meta?.account) {
      return false;
    }
    validateTenantScopedResource(slot, 'Slot', { organizationId, environment });
    const environmentTags = (slot.meta?.tag ?? []).filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM);
    if (environmentTags.length === 0) {
      return true;
    }
    if (environmentTags.length === 1 && environmentTags[0].code === environment) {
      return true;
    }
    throw new Error(`Slot/${slot.id ?? 'unknown'} must be scoped to environment ${environment}`);
  });
}

function validateV2OverrideSlot(
  slot: Slot,
  schedule: Schedule,
  timezone: string,
  healthcareServices: HealthcareService[],
  scope: { organizationId: string; environment: string },
  options: { allowProtectedStatus?: boolean } = {}
): void {
  validateScopedResource(slot, 'Slot', scope);
  validateOverrideSlotSchedule(slot, schedule);
  if (
    !['free', 'busy-unavailable'].includes(slot.status ?? '') &&
    !(options.allowProtectedStatus && slot.status && protectedOverrideSlotStatuses.has(slot.status))
  ) {
    throw new Error(`V2 override Slot/${slot.id ?? 'unknown'} has unsupported status ${slot.status ?? 'missing'}.`);
  }
  if (!slot.start || !slot.end || !Number.isFinite(Date.parse(slot.start)) || !Number.isFinite(Date.parse(slot.end))) {
    throw new Error(`V2 override Slot/${slot.id ?? 'unknown'} has invalid start or end.`);
  }
  if (Date.parse(slot.start) >= Date.parse(slot.end)) {
    throw new Error(`V2 override Slot/${slot.id ?? 'unknown'} must end after it starts.`);
  }
  const isProtectedStatus = Boolean(slot.status && protectedOverrideSlotStatuses.has(slot.status));
  if (
    !getOverrideSlotDraft(slot, timezone, healthcareServices) &&
    !(options.allowProtectedStatus && isProtectedStatus)
  ) {
    throw new Error(
      `V2 override Slot/${slot.id ?? 'unknown'} cannot map to an active HealthcareService in provider timezone ${timezone}; fix its serviceType, start, or end before saving.`
    );
  }
}

const protectedOverrideSlotStatuses = new Set(['active', 'booked', 'busy', 'tentative', 'fulfilled', 'busy-tentative']);

function getDateInTimezone(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10);
}

async function validateCurrentOverrideSlots(
  medplum: MedplumClient,
  expectedSlots: Slot[],
  updates: Slot[],
  deletes: Slot[],
  scope: { organizationId: string; environment: string },
  schedule: Schedule
): Promise<void> {
  const expectedById = new Map(expectedSlots.filter((slot) => slot.id).map((slot) => [slot.id, slot]));
  for (const requested of [...updates, ...deletes]) {
    if (!requested.id) {
      continue;
    }
    const expected = expectedById.get(requested.id);
    if (!expected) {
      throw new Error(`Override Slot/${requested.id} is not part of the loaded availability resources.`);
    }
    let current: Slot;
    try {
      current = await medplum.readResource('Slot', requested.id);
    } catch {
      throw new Error(`Override Slot/${requested.id} changed or was deleted while editing.`);
    }
    validateScopedResource(current, 'Slot', scope);
    validateOverrideSlotSchedule(current, schedule);
    if (current.status && protectedOverrideSlotStatuses.has(current.status)) {
      throw new Error(`Override Slot/${requested.id} cannot be changed while status is ${current.status}.`);
    }
    if (hasResourceChanged(expected, current)) {
      throw new Error(`Override Slot/${requested.id} changed while editing; reload before saving.`);
    }
  }
}

function rejectSlotOverlaps(desiredSlots: Slot[], allScheduleSlots: Slot[]): void {
  for (const desired of desiredSlots) {
    if (!desired.start || !desired.end) {
      continue;
    }
    const desiredStart = Date.parse(desired.start);
    const desiredEnd = Date.parse(desired.end);
    const conflict = allScheduleSlots.find((occupied) => {
      if (!occupied.start || !occupied.end || occupied.id === desired.id) {
        return false;
      }
      // Appointment Slots (the busy primary plus its untagged busy-unavailable
      // buffer) are immutable scheduling records. Their overlap is surfaced
      // through the Appointment conflict check, not rejected as an
      // availability-authoring collision.
      if (
        desired.status === 'busy-unavailable' &&
        (occupied.status === 'busy' || (occupied.status === 'busy-unavailable' && !isV2OverrideSlot(occupied)))
      ) {
        return false;
      }
      return (
        desiredStart < Date.parse(occupied.end) &&
        desiredEnd > Date.parse(occupied.start) &&
        slotsShareService(desired, occupied)
      );
    });
    if (conflict) {
      throw new Error(
        `${desired.status === 'free' ? 'Free' : 'Blocked'} override Slot/${desired.id ?? 'new'} overlaps ${conflict.status ?? 'missing'} Slot/${conflict.id ?? 'unknown'}.`
      );
    }
  }
}

async function findAppointmentConflictsInternal(
  medplum: MedplumClient,
  practitioner: Practitioner,
  draft: V2AvailabilityDraft,
  existingOverrideSlots: Slot[]
): Promise<import('./types').AppointmentConflict[]> {
  const organizationId = import.meta.env?.VITE_MEDPLUM_ORGANIZATION_ID;
  const environment = import.meta.env?.VITE_POLARIS_ENV;
  if (!organizationId || !environment || !practitioner.id) {
    throw new Error('Tenant organization, Polaris environment, and provider ID are required.');
  }
  const appointments = (await medplum.searchResources('Appointment', {
    actor: `Practitioner/${practitioner.id}`,
    _compartment: `Organization/${organizationId}`,
    _tag: `${POLARIS_ENV_TAG_SYSTEM}|${environment}`,
    _count: '1000',
  })) as Appointment[];
  // Only new or moved time off is checked: blocks saved before this rule existed may already sit over
  // appointments and must not lock the provider out of editing the rest of their availability.
  const existingById = new Map(existingOverrideSlots.flatMap((slot) => (slot.id ? [[slot.id, slot] as const] : [])));
  const proposed = draft.overrides
    .filter((override) => override.status === 'busy-unavailable')
    .map((override) => ({
      id: override.id,
      start: Date.parse(localDateTimeToFhir(override.date, override.startTime, draft.timezone)),
      end: Date.parse(localDateTimeToFhir(override.date, override.endTime, draft.timezone)),
    }))
    .filter((window) => {
      const existing = window.id ? existingById.get(window.id) : undefined;
      return (
        existing?.status !== 'busy-unavailable' ||
        Date.parse(existing.start ?? '') !== window.start ||
        Date.parse(existing.end ?? '') !== window.end
      );
    });
  if (proposed.length === 0) {
    return [];
  }
  return appointments.flatMap((appointment) => {
    if (!appointment.id || !appointment.start || !['booked', 'tentative'].includes(appointment.status ?? '')) {
      return [];
    }
    const start = Date.parse(appointment.start);
    const end = Date.parse(appointment.end ?? appointment.start);
    if (
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      !proposed.some((window) => start < window.end && end > window.start)
    ) {
      return [];
    }
    const patient = appointment.participant?.find((participant) =>
      participant.actor?.reference?.startsWith('Patient/')
    );
    return [
      {
        id: appointment.id,
        start: appointment.start,
        end: appointment.end,
        status: appointment.status,
        patientDisplay: patient?.actor?.display,
      },
    ];
  });
}

function slotsShareService(left: Slot, right: Slot): boolean {
  const leftServices = new Set(
    (left.serviceType ?? []).flatMap((serviceType) =>
      (serviceType.coding ?? []).map((coding) => `${coding.system ?? ''}|${coding.code ?? ''}`)
    )
  );
  const rightServices = (right.serviceType ?? []).flatMap((serviceType) =>
    (serviceType.coding ?? []).map((coding) => `${coding.system ?? ''}|${coding.code ?? ''}`)
  );

  if (leftServices.size === 0 || rightServices.length === 0) {
    return true;
  }
  return rightServices.some((service) => leftServices.has(service));
}

function ifMatchOptions(resource: { meta?: { versionId?: string } }): { headers: { 'If-Match': string } } | undefined {
  return resource.meta?.versionId ? { headers: { 'If-Match': `W/\"${resource.meta.versionId}\"` } } : undefined;
}

function hasResourceChanged(expected: Slot, current: Slot): boolean {
  if (expected.meta?.versionId || current.meta?.versionId) {
    if (expected.meta?.versionId !== current.meta?.versionId) {
      return true;
    }
  }
  if (expected.meta?.lastUpdated || current.meta?.lastUpdated) {
    if (expected.meta?.lastUpdated !== current.meta?.lastUpdated) {
      return true;
    }
  }
  return JSON.stringify(expected) !== JSON.stringify(current);
}

export function readOverrideDrafts(
  slots: Slot[],
  timezone: string,
  services: HealthcareService[]
): AvailabilityOverrideDraft[] {
  return slots.flatMap((slot) => {
    const draft = getOverrideSlotDraft(slot, timezone, services);
    return draft ? [draft] : [];
  });
}

export function isV2OverrideSlot(slot: Slot): boolean {
  return (
    slot.meta?.tag?.some((tag) => tag.system === V2_OVERRIDE_TAG_SYSTEM && tag.code === V2_OVERRIDE_TAG_CODE) ?? false
  );
}

export function describeCompatibleServices(services: HealthcareService[]): string[] {
  return services.map((service) => getServiceCode(service).display ?? service.name ?? service.id ?? 'Unnamed service');
}
